import { useEffect, useState, type ReactNode, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpenIcon,
  BookTextIcon,
  ClipboardListIcon,
  HelpCircleIcon,
  ListChecksIcon,
  MapPinIcon,
  SendHorizontalIcon,
  XIcon,
  type LucideIcon,
} from 'lucide-react';
import { useTenantBrand } from '../lib/tenant-brand.js';

/**
 * Invoerveld + richtingen van Mila op desktop, plus de begroeting en de
 * voorbeeldvragen die mobiel en desktop delen. Gekozen uit drie
 * desktop-prototypes op 10 sep 2026 (Jos, variant "mobiel vertaald"):
 * verzendknop in het veld, een gekozen richting blijft als chip staan tot de
 * vraag verstuurd is, zoekbereik op dezelfde regel als de richtingen.
 */

export type AskMode = 'free' | 'verdiepen' | 'werkvormen' | 'verwijzen' | 'plan';
export type Intent = Exclude<AskMode, 'free'>;

export const INTENTS: Array<{ mode: Intent; label: string; hint: string; Icon: LucideIcon }> = [
  {
    mode: 'verdiepen',
    label: 'Verdiepen',
    hint: 'Theorie en onderbouwing uit de bronnen',
    Icon: BookOpenIcon,
  },
  {
    mode: 'werkvormen',
    label: 'Werkvormen',
    hint: 'Oefeningen en werkbladen om direct in te zetten',
    Icon: ClipboardListIcon,
  },
  {
    mode: 'verwijzen',
    label: 'Verwijzen',
    hint: 'Passende zorgaanbieders uit de Sociale Kaart',
    Icon: MapPinIcon,
  },
  {
    mode: 'plan',
    label: 'Plan-opzet',
    hint: 'Algemene opzet bij een klachtenbeeld',
    Icon: ListChecksIcon,
  },
];

// Eén vraag per hoofddoel van Mila (werkvormen, methodes, tools, verwijzen),
// geformuleerd zoals een POH-GGZ ze in de spreekkamer stelt.
export const EXAMPLE_QUESTIONS = [
  'Welke werkvormen kan ik inzetten bij een cliënt met somberheid?',
  'Wat zeggen de bronnen over CGT en ACT bij aanhoudende angstklachten?',
  'Welk werkblad kan ik meegeven bij slaapproblemen?',
  'Wanneer verwijs ik door naar de basis-GGZ bij paniekklachten?',
];

export const PII_NOTE =
  'Geen namen of herleidbare patiëntgegevens invoeren. Vragen worden opgeslagen in je chatgeschiedenis.';

export function MilaGreeting({ compact = false }: { compact?: boolean }): JSX.Element {
  const { kennisbank } = useTenantBrand();
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <h1
          className={`font-display font-bold text-ink-900 ${compact ? 'text-[22px]' : 'text-[26px]'}`}
        >
          Hoi, ik ben Mila.
        </h1>
        <p className="mt-1 max-w-xl text-[15px] leading-relaxed text-ink-900/60">
          Stel een vraag over de {kennisbank}. Antwoorden komen uitsluitend uit geverifieerde
          bronnen, met bronkaartjes erbij.
        </p>
      </div>
      {!compact && (
        <Link
          to="/help"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1.5 text-[13px] font-medium text-ink-900/70 hover:bg-brand-50"
        >
          <HelpCircleIcon size={15} /> Uitleg
        </Link>
      )}
    </div>
  );
}

export function ExampleQuestions({ onPick }: { onPick: (question: string) => void }): JSX.Element {
  return (
    <div>
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-900/40">
        Voorbeeldvragen
      </p>
      <div className="space-y-2">
        {EXAMPLE_QUESTIONS.map((q) => (
          <button
            key={q}
            type="button"
            onClick={() => onPick(q)}
            className="block w-full rounded-xl border border-ring2 bg-white px-4 py-3 text-left text-[14px] text-ink-900 shadow-card hover:border-brand-700"
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}

export interface KennisagentComposerProps {
  draft: string;
  setDraft: (v: string) => void;
  inputRef: RefObject<HTMLTextAreaElement>;
  submit: (mode: AskMode) => void;
  pending: boolean;
  hasTurns: boolean;
  /** Sticky modus uit een Sara-sessie; wint van de vrije modus. */
  stickyMode: AskMode | null;
  /** Sara-contextbalk (of null); staat bovenin het veld. */
  banner: ReactNode;
  scopeLabel: string;
  scopeActive: boolean;
  onOpenScope: () => void;
  onClearScope: () => void;
}

export function KennisagentComposer(p: KennisagentComposerProps): JSX.Element {
  const [armed, setArmed] = useState<Intent | null>(null);
  const armedIntent = INTENTS.find((i) => i.mode === armed);
  const canSend = !p.pending && (p.draft.trim().length > 0 || armed !== null);

  const send = (): void => {
    if (!canSend) return;
    p.submit(armed ?? p.stickyMode ?? 'free');
    setArmed(null);
  };

  useEffect(() => {
    const el = p.inputRef.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [p.draft, p.inputRef]);

  return (
    <div className="space-y-3">
      <div className="rounded-3xl border border-ring2 bg-white shadow-card focus-within:border-brand-700">
        {p.banner && <div className="px-3 pt-3">{p.banner}</div>}
        {armedIntent && (
          <div className="flex items-center gap-1.5 px-4 pt-3 text-[12px] font-medium text-brand-800">
            <armedIntent.Icon size={13} /> {armedIntent.label}
            <button
              type="button"
              onClick={() => setArmed(null)}
              aria-label="Richting loslaten"
              className="ml-1 text-ink-900/40"
            >
              <XIcon size={13} />
            </button>
          </div>
        )}
        <div className="flex items-end gap-2 py-2 pr-2">
          <textarea
            ref={p.inputRef}
            value={p.draft}
            onChange={(e) => p.setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            disabled={p.pending}
            maxLength={4000}
            rows={1}
            placeholder={
              armed
                ? 'Beschrijf de situatie of het onderwerp…'
                : p.hasTurns
                  ? 'Vervolgvraag…'
                  : 'Stel je vraag aan Mila…'
            }
            className="min-h-[40px] w-full resize-none bg-transparent px-4 py-2 text-[16px] text-ink-900 placeholder:text-ink-900/35 focus:outline-none disabled:opacity-60"
          />
          <button
            type="button"
            onClick={send}
            disabled={!canSend}
            aria-label="Vraag stellen"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-800 text-white transition-colors hover:bg-brand-700 disabled:opacity-40"
          >
            <SendHorizontalIcon size={18} />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {INTENTS.map(({ mode, label, hint, Icon }) => (
            <button
              key={mode}
              type="button"
              disabled={p.pending}
              title={hint}
              onClick={() => {
                setArmed(armed === mode ? null : mode);
                p.inputRef.current?.focus();
              }}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                armed === mode
                  ? 'border-brand-700 bg-brand-50 text-brand-800'
                  : 'border-ring2 bg-white text-ink-900 hover:border-brand-700'
              }`}
            >
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
        <span className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={p.onOpenScope}
            title="Beperk de zoekopdracht tot specifieke publicaties"
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] ${
              p.scopeActive
                ? 'border-brand-700 bg-brand-50 text-brand-800'
                : 'border-ring2 bg-white text-ink-900/60 hover:border-brand-700'
            }`}
          >
            <BookTextIcon size={12} /> Zoekbereik: {p.scopeLabel}
          </button>
          {p.scopeActive && (
            <button
              type="button"
              onClick={p.onClearScope}
              className="text-ink-900/45 hover:text-ink-900"
              title="Reset zoekbereik naar alle publicaties"
            >
              <XIcon size={12} />
            </button>
          )}
        </span>
      </div>

      <p className="text-[11px] text-ink-900/45">
        {PII_NOTE} Mila geeft geen diagnose of medicatieadvies.
      </p>
    </div>
  );
}
